import { ErrorOverlay } from "@/components/overlay/ErrorOverlay";
import {
  PublicAppStore,
  useAppStore,
  useInitializeAppStore,
  useInitializeGuestAppStore,
  usePublicAppStore,
} from "@/store";
import { QuickSearchModal } from "@/store/pages/SearchPageStore/QuickSearchModal/QuickSearchModal";
import { AppStoreContext, GuestAppStoreContext } from "@/store/utils/context";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { localStorageModule } from "@/modules/local-storage";
import { Outlet } from "react-router";
import { LoadingOverlay } from "@/components/overlay";
import { SyncStoreInitializationChecker } from "@/app/overlays/SyncStoreInitializationChecker";
import { BootstrapOverlay } from "@/app/overlays/BootstrapOverlay";
import { LogoutOverlay } from "@/app/overlays/LogoutOverlay";
import { UpgradedUserEmailChecker } from "@/app/overlays/UpgradedUserEmailChecker";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { UpsellModal } from "@/components/modal/upsell";
import { WelcomeToProModal } from "@/components/modal/welcome-to-pro/welcome-to-pro";
import { FeatureGateBannerQuotaLevel } from "@/components/banners/feature-gate-banner/feature-gate-banner.types";
import {
  clearImportSessionInProgress,
  readImportSessionsInProgress,
} from "@/modules/cache/import-session";
import { trackConversionWithRewardful } from "@/modules/rewardful";
import { useTheme } from "@/mds";

interface OutletLoadingHandlers {
  setIsReady: () => void;
  setIsError: () => void;
  setLoadingMessage: (message: string) => void;
}

interface StoreProviderOutletProps {}

export const StoreProviderOutlet: FC<StoreProviderOutletProps> = observer(() => {
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");

  const setIsReady = () => setShowLoadingOverlay(false);
  const setIsError = () => setShowErrorOverlay(true);

  return (
    <>
      <ErrorOverlay isVisible={showErrorOverlay} />
      <LoadingOverlay isVisible={showLoadingOverlay} loadingMessage={loadingMessage} />
      <StoreProviderOutletContent
        setIsReady={setIsReady}
        setIsError={setIsError}
        setLoadingMessage={setLoadingMessage}
      />
    </>
  );
});

export const StoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { publicStore } = usePublicAppStore();

    if (publicStore.auth.isGuestMode) {
      return (
        <GuestModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    if (publicStore.auth.isStandardMode) {
      return (
        <StandardModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    /**
     * If the user is not in guest mode or standard mode, we
     * don't wrap the outlet in any store providers.
     */
    return (
      <>
        <Outlet />
      </>
    );
  }
);

const GuestModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError }) => {
    const state = useInitializeGuestAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      setIsReady();
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <GuestAppStoreContext.Provider value={state.data}>
        <Outlet />
      </GuestAppStoreContext.Provider>
    );
  });

const StandardModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError, setLoadingMessage }) => {
    const state = useInitializeAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      /**
       * Note that we don't call `setIsReady` here because we also need
       * the StandardModeStoreProviderOutletContent to be ready.
       */
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <AppStoreContext.Provider value={state.data}>
        <StandardModeStoreProviderOutletContent
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      </AppStoreContext.Provider>
    );
  });

/**
 * We use this to kick-off global side effects in a hook.
 */
const StandardModeStoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { store, pageStore } = useAppStore();
    const { themeType, setTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    store.useInitializeAppStoreEffects();
    pageStore.useInitializePageStoreEffects();

    useEffect(() => {
      if (store.readyState.error) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Error.",
          info: { error: objectModule.safeErrorAsJson(store.readyState.error) },
        });

        return;
      }

      if (store.readyState.loading) {
        return;
      }

      if (!store.readyState.data) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Missing data.",
        });

        return;
      }

      setIsReady();
    }, [
      setIsError,
      setIsReady,
      store.readyState.data,
      store.readyState.error,
      store.readyState.loading,
    ]);

    // Coerce dark theme to light for free users after store is ready
    useEffect(() => {
      if (!store.readyState.data) return;
      const isAllowedDark = store.subscriptions.personalSubscription?.isPaid ?? false;

      if (themeType === "harvest-moon" && !isAllowedDark) {
        setTheme("harvest");
      }
    }, [
      setTheme,
      store.readyState.data,
      store.subscriptions.personalSubscription?.isPaid,
      themeType,
    ]);

    // Reconcile any import sessions left in-progress after a hard refresh
    useEffect(() => {
      if (!store.readyState.data) return;

      const cleanupInProgressImportSessions = async () => {
        const markers = await readImportSessionsInProgress();
        const sessionIds = new Set(markers.map(m => m.sessionId));

        for (const m of markers) {
          try {
            await store.sessions.updateSessionOnNewUserMessage(m.sessionId, { status: "ERROR" });
          } catch {
            // ignore per-session failure
          } finally {
            await clearImportSessionInProgress(m.sessionId);
          }
        }

        for (const sessionId of sessionIds) {
          store.sessionEvents.createMessageSentSessionEvent({
            session_id: sessionId,
            message_value: {
              kind: "REASONING",
              content: "Uploads were interrupted",
            },
          });

          trackEvent(TrackedEvent.ImportFilesBatchFailed, {
            session_id: sessionId,
            context: "StandardModeStoreProviderOutletContent",
          });
        }
      };

      cleanupInProgressImportSessions();
    }, [store.readyState.data, store.sessionEvents, store.sessions]);

    const bootstrapProgressMessage = store.sync.bootstrapProgressMessage;
    useEffect(() => {
      setLoadingMessage(bootstrapProgressMessage);
    }, [bootstrapProgressMessage, setLoadingMessage]);

    // Handle Stripe success/cancel params globally and strip them immediately
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const stripeSuccess = params.get("stripe_success");
      const sessionId = params.get("session_id");
      const stripeCancel = params.get("stripe_cancel");

      if (stripeSuccess === "1" || (sessionId && sessionId.startsWith("cs_"))) {
        store.platformInfo.stripeSubscriptionCompletedSuccessfully();
        store.platformInfo.openWelcomeToProModal();
        const email = store.account.myAccount.profileEmailAddress;
        if (email) trackConversionWithRewardful({ email });
        if (sessionId) {
          trackEvent(TrackedEvent.PaywallUpgradeReturn, {
            stripe_session_id: sessionId,
          });
          localStorageModule.writeStripeLastCheckoutSession(sessionId);
        }

        const next = new URLSearchParams(location.search);
        next.delete("stripe_success");
        next.delete("session_id");
        navigate(location.pathname + (next.size ? `?${next.toString()}` : ""), {
          replace: true,
        });
      } else if (stripeCancel === "1") {
        trackEvent(TrackedEvent.PaywallCancelReturn, {});
        const next = new URLSearchParams(location.search);
        next.delete("stripe_cancel");
        navigate(location.pathname + (next.size ? `?${next.toString()}` : ""), {
          replace: true,
        });
      }
    }, [
      location.pathname,
      location.search,
      navigate,
      store.platformInfo,
      store.account.myAccount.profileEmailAddress,
    ]);

    if (!store.readyState.data) {
      return null;
    }

    return (
      <>
        <SyncStoreInitializationChecker />
        <BootstrapOverlay />
        <LogoutOverlay />
        <UpgradedUserEmailChecker />
        <QuickSearchModal />
        {store.subscriptions.upsellModalIsOpen ? (
          <UpsellModal
            isOpen={store.subscriptions.upsellModalIsOpen}
            onClose={store.subscriptions.closeUpsell}
            gatedFeature={store.subscriptions.upsellModalFeature}
            level={FeatureGateBannerQuotaLevel.Exceeded}
          />
        ) : null}
        <WelcomeToProModal
          isOpen={store.platformInfo.welcomeToProModalIsOpen}
          onClose={store.platformInfo.closeWelcomeToProModal}
        />
        <Outlet />
      </>
    );
  }
);
